/**
 * Leaf module — rolling window of Supabase HTTP fetch outcomes.
 * Used to infer reachability (association != internet).
 */

const DEGRADED_WINDOW_MS = 60_000;
const UNREACHABLE_WINDOW_MS = 120_000;
const MIN_FAILURES_FOR_UNREACHABLE = 3;

const listeners = new Set();

/** @type {{ at: number, ok: boolean, timeout: boolean }[]} */
let outcomes = [];

function pruneOutcomes(now = Date.now(), windowMs = DEGRADED_WINDOW_MS) {
  outcomes = outcomes.filter((o) => now - o.at <= windowMs);
}

function getOutcomesInWindow(windowMs, now = Date.now()) {
  return outcomes.filter((o) => now - o.at <= windowMs);
}

function emitChange() {
  listeners.forEach((listener) => listener());
}

/** Drop stale offline failures when wifi/cellular re-associates so RQ can resume promptly. */
export function noteNetworkReassociated() {
  outcomes = outcomes.filter((o) => o.ok);
  emitChange();
}

export function recordFetchOutcome({ ok, timeout = false }) {
  outcomes.push({ at: Date.now(), ok: Boolean(ok), timeout: Boolean(timeout) });
  pruneOutcomes(undefined, UNREACHABLE_WINDOW_MS);
  emitChange();
}

/** Congested / flaky — transient timeouts and elevated failure rate (60s window). */
export function getFetchHealth() {
  const recent = getOutcomesInWindow(DEGRADED_WINDOW_MS);
  if (recent.length === 0) return 'unknown';

  const failures = recent.filter((o) => !o.ok);
  const timeouts = failures.filter((o) => o.timeout);

  if (recent.some((o) => o.ok)) return 'good';
  if (timeouts.length >= 3) return 'degraded';
  if (recent.length >= 5 && failures.length / recent.length >= 0.6) return 'degraded';
  if (failures.length >= 4) return 'degraded';
  return 'unknown';
}

/** Sustained total failure — no successes over 120s with multiple failures. */
export function hasSustainedFetchFailure(now = Date.now()) {
  const recent = getOutcomesInWindow(UNREACHABLE_WINDOW_MS, now);
  if (recent.some((o) => o.ok)) return false;
  const failures = recent.filter((o) => !o.ok);
  return failures.length >= MIN_FAILURES_FOR_UNREACHABLE;
}

export function getRecentFetchStats() {
  const recent = getOutcomesInWindow(DEGRADED_WINDOW_MS);
  const failures = recent.filter((o) => !o.ok);
  return {
    sampleCount: recent.length,
    failureCount: failures.length,
    timeoutCount: failures.filter((o) => o.timeout).length,
    lastOutcome: outcomes[outcomes.length - 1] ?? null,
    sustainedFailure: hasSustainedFetchFailure(),
  };
}

export function subscribeFetchHealth(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function resetFetchOutcomesForTests() {
  outcomes = [];
  emitChange();
}
